/**
 * Pick which action-items list notice to show when several are eligible.
 *
 * Priority (highest first):
 * 1. usage_limit — blocks AI features
 * 2. catch_up — session digest on return
 * 3. aging — quiet-task housekeeping
 * 4. credits_restored — one-shot confirmation
 *
 * Only one notice should render at a time (no stacked / overlaid strips).
 * usage_limit and credits_restored are mutually exclusive in practice;
 * usage_limit still wins if both flags are somehow true.
 */

export const ACTION_ITEMS_LIST_NOTICE = Object.freeze({
  USAGE_LIMIT: "usage_limit",
  CATCH_UP: "catch_up",
  AGING: "aging",
  CREDITS_RESTORED: "credits_restored",
});

/**
 * Resolve the single list notice id to render, or null when none apply.
 *
 * @example
 * resolveActionItemsListNotice({
 *   usageLimitReached: true,
 *   catchUpEligible: true,
 *   agingEligible: true,
 *   creditsRestoredEligible: true,
 * });
 * // "usage_limit"
 *
 * @example
 * resolveActionItemsListNotice({
 *   usageLimitReached: false,
 *   catchUpEligible: false,
 *   agingEligible: true,
 *   creditsRestoredEligible: true,
 * });
 * // "aging"
 *
 * @example
 * resolveActionItemsListNotice({});
 * // null
 *
 * @param {object} flags
 * @param {boolean} [flags.usageLimitReached]
 * @param {boolean} [flags.catchUpEligible]
 * @param {boolean} [flags.agingEligible]
 * @param {boolean} [flags.creditsRestoredEligible]
 * @returns {string|null}
 */
export const resolveActionItemsListNotice = ({
  usageLimitReached = false,
  catchUpEligible = false,
  agingEligible = false,
  creditsRestoredEligible = false,
} = {}) => {
  if (usageLimitReached) return ACTION_ITEMS_LIST_NOTICE.USAGE_LIMIT;
  if (catchUpEligible) return ACTION_ITEMS_LIST_NOTICE.CATCH_UP;
  if (agingEligible) return ACTION_ITEMS_LIST_NOTICE.AGING;
  if (creditsRestoredEligible) return ACTION_ITEMS_LIST_NOTICE.CREDITS_RESTORED;
  return null;
};
